import { useState } from 'react';
import { graphql } from 'react-relay';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { expValEquals } from '@confluence/feature-experiments';

import type {
	usePendingRequestExistsQuery as usePendingRequestExistsQueryType,
	usePendingRequestExistsQueryVariables,
} from './__types__/usePendingRequestExistsQuery.ts';
const UsePendingRequestExistsQuery = graphql`
	query usePendingRequestExistsQuery(
		$accessRequestedAaid: String!
		$cloudId: ID!
		$contentId: ID!
	) {
		confluence_pendingRequestExists(
			accessRequestedAaid: $accessRequestedAaid
			cloudId: $cloudId
			contentId: $contentId
		) {
			isPendingAccessRequestExists
		}
	}
`;

export const usePendingRequestExists = ({
	contentId,
}: {
	contentId: string | null | undefined;
}) => {
	const { userId, cloudId } = useSessionData();

	const shouldSkip =
		!userId ||
		!cloudId ||
		!contentId ||
		!expValEquals('confluence_frontend_existing_access_request_checks', 'cohort', 'experiment');

	const [isLoadingForFirstTime, setIsLoadingForFirstTime] = useState(!shouldSkip);

	const { data, error, loading } = useQueryInterop<
		usePendingRequestExistsQueryType,
		usePendingRequestExistsQueryVariables
	>(UsePendingRequestExistsQuery, {
		variables: {
			cloudId,
			accessRequestedAaid: userId ?? '',
			contentId: contentId ?? '',
		},
		skip: shouldSkip,
		fetchPolicy: 'network-only',
		onCompleted: () => {
			setIsLoadingForFirstTime(false);
		},
	});

	const pendingRequestExists = Boolean(
		data?.confluence_pendingRequestExists?.isPendingAccessRequestExists,
	);

	return { pendingRequestExists, loading, error, isLoadingForFirstTime };
};
